import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminTenantsApi } from '../../services/api';
import { toast } from '../../stores';
import {
  NexusCard, NexusButton, NexusInput, NexusSelect,
  NexusModal, NexusModalFooter, NexusBadge, NexusTable,
} from '../../components/nexus-ui';
import { Plus, Trash2, Users, ChevronRight, Search, Building2, UserPlus, X, Globe, Link2, Copy, RefreshCw } from 'lucide-react';

const PLAN_OPTIONS = [
  { value: 'free', label: 'Free' },
  { value: 'starter', label: 'Starter' },
  { value: 'professional', label: 'Professional' },
  { value: 'enterprise', label: 'Enterprise' },
];

const PLAN_COLORS = {
  free: 'info',
  starter: 'success',
  professional: 'premium',
  enterprise: 'error',
};

const AdminTenants = () => {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [searchFilter, setSearchFilter] = useState('');
  const [showAddUser, setShowAddUser] = useState(false);
  const [tenantUsers, setTenantUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [userForm, setUserForm] = useState({ name: '', email: '', password: '', role: 'member' });
  const [domainInput, setDomainInput] = useState('');
  const [savingDomain, setSavingDomain] = useState(false);
  const [rotatingShare, setRotatingShare] = useState(false);
  const [copiedShare, setCopiedShare] = useState(false);
  const [form, setForm] = useState({
    name: '', slug: '', industry: '', billing_email: '', plan_tier: 'free',
    admin_name: '', admin_email: '', admin_password: '',
  });

  const { data: tenants, isLoading } = useQuery({
    queryKey: ['tenants'],
    queryFn: () => adminTenantsApi.list().then(r => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (data) => adminTenantsApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success('Tenant created');
      setShowCreate(false);
      setForm({ name: '', slug: '', industry: '', billing_email: '', plan_tier: 'free', admin_name: '', admin_email: '', admin_password: '' });
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminTenantsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast.success('Tenant deleted');
      setSelectedTenant(null);
    },
  });

  const createUserMutation = useMutation({
    mutationFn: ({ tenantId, data }) => adminTenantsApi.createUser(tenantId, data),
    onSuccess: () => {
      toast.success('User created');
      setShowAddUser(false);
      setUserForm({ name: '', email: '', password: '', role: 'member' });
      if (selectedTenant) fetchTenantUsers(selectedTenant.id);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to create user'),
  });

  const deleteUserMutation = useMutation({
    mutationFn: ({ tenantId, userId }) => adminTenantsApi.deleteUser(tenantId, userId),
    onSuccess: () => {
      toast.success('User deleted');
      if (selectedTenant) fetchTenantUsers(selectedTenant.id);
    },
  });

  const fetchTenantUsers = async (tenantId) => {
    setLoadingUsers(true);
    try {
      const res = await adminTenantsApi.listUsers(tenantId);
      setTenantUsers(res.data);
    } catch {
      setTenantUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    if (selectedTenant) {
      fetchTenantUsers(selectedTenant.id);
      setDomainInput(selectedTenant.custom_domain || '');
    } else {
      setTenantUsers([]);
      setShowAddUser(false);
      setDomainInput('');
    }
  }, [selectedTenant]);

  const filtered = (tenants || []).filter(t =>
    t.name?.toLowerCase().includes(searchFilter.toLowerCase()) ||
    t.industry?.toLowerCase().includes(searchFilter.toLowerCase())
  );

  const columns = [
    {
      key: 'name',
      header: 'Tenant',
      render: (_, row) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-nexus-accent-violet/10 flex items-center justify-center">
            <Building2 size={16} className="text-nexus-accent-violet" />
          </div>
          <div>
            <p className="font-medium text-nexus-text-primary text-sm">{row.name}</p>
            {row.industry && <p className="text-xs text-nexus-text-tertiary">{row.industry}</p>}
          </div>
        </div>
      ),
    },
    { key: 'industry', header: 'Industry', render: (v) => <span className="text-sm text-nexus-text-secondary">{v}</span> },
    {
      key: 'plan_tier', header: 'Plan',
      render: (v) => <NexusBadge variant={PLAN_COLORS[v] || 'info'}>{v}</NexusBadge>,
    },
    {
      key: 'is_active', header: 'Status',
      render: (v) => <NexusBadge variant={v ? 'success' : 'error'} pulse={v}>{v ? 'Active' : 'Inactive'}</NexusBadge>,
    },
    {
      key: 'max_users', header: 'Users',
      render: (v) => <span className="text-sm text-nexus-text-secondary">{v}</span>,
    },
    {
      key: 'actions', header: '',
      render: (_, row) => (
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={(e) => { e.stopPropagation(); setSelectedTenant(row); }}
            className="p-1.5 rounded-lg hover:bg-nexus-bg-tertiary/50 text-nexus-text-tertiary hover:text-nexus-text-primary"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      ),
    },
  ];

  const autoSlug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleCreateTenant = () => {
    const payload = {
      name: form.name,
      slug: form.slug,
      industry: form.industry,
      billing_email: form.billing_email,
      plan_tier: form.plan_tier,
    };
    if (form.admin_name && form.admin_email && form.admin_password) {
      payload.admin_name = form.admin_name;
      payload.admin_email = form.admin_email;
      payload.admin_password = form.admin_password;
    }
    createMutation.mutate(payload);
  };

  return (
    <div className="space-y-6" data-testid="admin-tenants">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-nexus-text-primary">Tenant Management</h2>
          <p className="text-nexus-text-secondary text-sm mt-1">Manage customer organizations and their plans</p>
        </div>
        <NexusButton onClick={() => setShowCreate(true)} data-testid="create-tenant-btn">
          <Plus size={16} /> Add Tenant
        </NexusButton>
      </div>

      {/* Search Bar */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-nexus-text-tertiary" />
        <input
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          placeholder="Search tenants..."
          className="w-full pl-10 pr-4 py-2.5 bg-nexus-input border border-nexus-border-subtle rounded-lg text-sm text-nexus-text-primary placeholder:text-nexus-text-tertiary focus:outline-none focus:border-nexus-accent-mint/30"
          data-testid="tenant-search"
        />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {['free', 'starter', 'professional', 'enterprise'].map((plan) => {
          const count = (tenants || []).filter(t => t.plan_tier === plan).length;
          return (
            <motion.div key={plan} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
              <NexusCard variant="glass" className="p-4">
                <p className="text-xs text-nexus-text-tertiary uppercase tracking-wider">{plan}</p>
                <p className="text-2xl font-heading font-bold text-nexus-text-primary mt-1">{count}</p>
              </NexusCard>
            </motion.div>
          );
        })}
      </div>

      {/* Tenant Table */}
      <NexusCard variant="glass" hover={false}>
        <div className="p-6">
          <NexusTable
            columns={columns}
            data={filtered}
            loading={isLoading}
            emptyMessage="No tenants found"
            onRowClick={(row) => setSelectedTenant(row)}
          />
        </div>
      </NexusCard>

      {/* Tenant Detail Drawer */}
      <AnimatePresence>
        {selectedTenant && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-50 flex justify-end"
            onClick={() => setSelectedTenant(null)}
          >
            <motion.div
              initial={{ x: 400 }}
              animate={{ x: 0 }}
              exit={{ x: 400 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="w-full max-w-md bg-nexus-bg-secondary border-l border-nexus-border-emphasized h-full overflow-y-auto p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-xl font-bold text-nexus-text-primary">{selectedTenant.name}</h3>
                <NexusBadge variant={selectedTenant.is_active ? 'success' : 'error'}>
                  {selectedTenant.is_active ? 'Active' : 'Inactive'}
                </NexusBadge>
              </div>
              <div className="space-y-4">
                {[
                  ['Industry', selectedTenant.industry],
                  ['Plan', selectedTenant.plan_tier],
                  ['Max Users', selectedTenant.max_users],
                  ['Monthly Tokens', (selectedTenant.max_tokens_monthly || 0).toLocaleString()],
                  ['Billing Email', selectedTenant.billing_email],
                  ['Created', new Date(selectedTenant.created_at).toLocaleDateString()],
                ].map(([label, value]) => (
                  <div key={label} className="flex justify-between py-2 border-b border-nexus-border-subtle">
                    <span className="text-sm text-nexus-text-tertiary">{label}</span>
                    <span className="text-sm text-nexus-text-primary font-medium">{value}</span>
                  </div>
                ))}
              </div>

              {/* Users Section */}
              <div className="mt-8">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-heading text-base font-semibold text-nexus-text-primary flex items-center gap-2">
                    <Users size={16} /> Users ({tenantUsers.length}/{selectedTenant.max_users})
                  </h4>
                  <button
                    onClick={() => setShowAddUser(!showAddUser)}
                    className="flex items-center gap-1 text-xs text-nexus-accent-mint hover:text-nexus-accent-mint/80 transition-colors"
                    data-testid="add-user-toggle-btn"
                  >
                    {showAddUser ? <X size={14} /> : <UserPlus size={14} />}
                    {showAddUser ? 'Cancel' : 'Add User'}
                  </button>
                </div>

                {/* Add User Form */}
                <AnimatePresence>
                  {showAddUser && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="overflow-hidden"
                    >
                      <div className="p-4 rounded-lg bg-nexus-bg-tertiary border border-nexus-border-subtle mb-4 space-y-3">
                        <NexusInput
                          label="Name"
                          value={userForm.name}
                          onChange={(e) => setUserForm(p => ({ ...p, name: e.target.value }))}
                          placeholder="John Doe"
                          data-testid="add-user-name"
                        />
                        <NexusInput
                          label="Email"
                          type="email"
                          value={userForm.email}
                          onChange={(e) => setUserForm(p => ({ ...p, email: e.target.value }))}
                          placeholder="john@company.com"
                          data-testid="add-user-email"
                        />
                        <NexusInput
                          label="Password"
                          type="password"
                          value={userForm.password}
                          onChange={(e) => setUserForm(p => ({ ...p, password: e.target.value }))}
                          placeholder="Minimum 6 characters"
                          data-testid="add-user-password"
                        />
                        <NexusSelect
                          label="Role"
                          options={[
                            { value: 'admin', label: 'Admin' },
                            { value: 'member', label: 'Member' },
                            { value: 'viewer', label: 'Viewer' },
                          ]}
                          value={userForm.role}
                          onChange={(v) => setUserForm(p => ({ ...p, role: v }))}
                        />
                        <NexusButton
                          onClick={() => createUserMutation.mutate({ tenantId: selectedTenant.id, data: userForm })}
                          isLoading={createUserMutation.isPending}
                          className="w-full"
                          data-testid="add-user-submit"
                        >
                          <UserPlus size={14} /> Create User
                        </NexusButton>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Users List */}
                {loadingUsers ? (
                  <div className="text-center py-4 text-nexus-text-tertiary text-sm">Loading users...</div>
                ) : tenantUsers.length === 0 ? (
                  <div className="text-center py-4 text-nexus-text-tertiary text-sm">No users yet. Add one above.</div>
                ) : (
                  <div className="space-y-2">
                    {tenantUsers.map((u) => (
                      <div key={u.id} className="flex items-center justify-between p-3 rounded-lg bg-nexus-bg-tertiary border border-nexus-border-subtle">
                        <div>
                          <p className="text-sm font-medium text-nexus-text-primary">{u.name}</p>
                          <p className="text-xs text-nexus-text-tertiary">{u.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <NexusBadge variant={u.role === 'admin' ? 'premium' : 'info'}>{u.role}</NexusBadge>
                          <button
                            onClick={() => {
                              if (confirm(`Delete user ${u.email}?`)) {
                                deleteUserMutation.mutate({ tenantId: selectedTenant.id, userId: u.id });
                              }
                            }}
                            className="p-1 rounded hover:bg-red-500/10 text-nexus-text-tertiary hover:text-red-400 transition-colors"
                            data-testid={`delete-user-${u.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Public Chat Link Section */}
              <div className="mt-8">
                <h4 className="font-heading text-base font-semibold text-nexus-text-primary flex items-center gap-2 mb-4">
                  <Link2 size={16} /> Public Chat Link
                </h4>
                <div className="p-4 rounded-lg bg-nexus-bg-tertiary border border-nexus-border-subtle space-y-3" data-testid="share-link-section">
                  {selectedTenant.share_code ? (() => {
                    const url = `${window.location.origin}/c/${selectedTenant.share_code}`;
                    return (
                      <>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 min-w-0 truncate px-3 py-2 rounded-md text-xs text-nexus-text-primary bg-nexus-bg-primary border border-nexus-border-subtle font-mono" data-testid="share-link-url">
                            {url}
                          </code>
                          <NexusButton
                            variant="secondary"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(url);
                                setCopiedShare(true);
                                toast.success('Link copied');
                                setTimeout(() => setCopiedShare(false), 1800);
                              } catch {
                                toast.error('Copy failed');
                              }
                            }}
                            className="flex-shrink-0"
                            data-testid="copy-share-link-btn"
                          >
                            <Copy size={14} /> {copiedShare ? 'Copied' : 'Copy'}
                          </NexusButton>
                          <NexusButton
                            variant="ghost"
                            onClick={async () => {
                              if (!confirm('Rotate public link? The current URL will stop working immediately and anyone with the old link will need the new one.')) return;
                              setRotatingShare(true);
                              try {
                                const res = await adminTenantsApi.rotateShareCode(selectedTenant.id);
                                setSelectedTenant(prev => ({ ...prev, share_code: res.data.share_code }));
                                queryClient.invalidateQueries({ queryKey: ['tenants'] });
                                toast.success('Share link rotated');
                              } catch (err) {
                                toast.error(err.response?.data?.detail || 'Failed to rotate');
                              } finally {
                                setRotatingShare(false);
                              }
                            }}
                            isLoading={rotatingShare}
                            className="flex-shrink-0 text-nexus-accent-violet"
                            data-testid="rotate-share-link-btn"
                            title="Rotate — generate a new code; old link will 404"
                          >
                            <RefreshCw size={14} /> Rotate
                          </NexusButton>
                        </div>
                        <p className="text-[11px] text-nexus-text-tertiary">
                          Anyone with this link can chat as an anonymous consumer. Rotate to revoke the current URL.
                        </p>
                      </>
                    );
                  })() : (
                    <p className="text-xs text-nexus-text-tertiary">No share code generated yet.</p>
                  )}
                </div>
              </div>

              {/* Domain Mapping Section */}
              <div className="mt-8">
                <h4 className="font-heading text-base font-semibold text-nexus-text-primary flex items-center gap-2 mb-4">
                  <Globe size={16} /> Custom Domain
                </h4>
                <div className="p-4 rounded-lg bg-nexus-bg-tertiary border border-nexus-border-subtle space-y-3">
                  <NexusInput
                    label="Domain"
                    value={domainInput}
                    onChange={(e) => setDomainInput(e.target.value)}
                    placeholder="chat.yourcompany.com"
                    data-testid="domain-input"
                  />
                  <div className="flex gap-2">
                    <NexusButton
                      onClick={async () => {
                        if (!domainInput.trim()) return;
                        setSavingDomain(true);
                        try {
                          const res = await adminTenantsApi.setDomain(selectedTenant.id, domainInput.trim());
                          toast.success('Domain mapping saved');
                          setSelectedTenant(prev => ({ ...prev, custom_domain: domainInput.trim() }));
                          queryClient.invalidateQueries({ queryKey: ['tenants'] });
                        } catch (err) {
                          toast.error(err.response?.data?.detail || 'Failed to save domain');
                        } finally {
                          setSavingDomain(false);
                        }
                      }}
                      isLoading={savingDomain}
                      className="flex-1"
                      data-testid="save-domain-btn"
                    >
                      Save Domain
                    </NexusButton>
                    {selectedTenant.custom_domain && (
                      <NexusButton
                        variant="ghost"
                        onClick={async () => {
                          try {
                            await adminTenantsApi.removeDomain(selectedTenant.id);
                            toast.success('Domain removed');
                            setDomainInput('');
                            setSelectedTenant(prev => ({ ...prev, custom_domain: null }));
                            queryClient.invalidateQueries({ queryKey: ['tenants'] });
                          } catch {
                            toast.error('Failed to remove domain');
                          }
                        }}
                        className="text-red-400"
                        data-testid="remove-domain-btn"
                      >
                        Remove
                      </NexusButton>
                    )}
                  </div>
                  {selectedTenant.custom_domain && (
                    <div className="p-3 rounded-lg bg-nexus-accent-mint/5 border border-nexus-accent-mint/20 text-xs text-nexus-text-secondary space-y-1">
                      <p className="font-medium text-nexus-accent-mint">DNS Setup Required</p>
                      <p>Add a CNAME record pointing <code className="text-nexus-text-primary">{selectedTenant.custom_domain}</code> to your platform domain.</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-8 pt-4 border-t border-nexus-border-subtle">
                <NexusButton
                  variant="ghost"
                  onClick={() => {
                    if (confirm('Delete this tenant and all its data?')) {
                      deleteMutation.mutate(selectedTenant.id);
                    }
                  }}
                  className="text-red-400 hover:text-red-300"
                  data-testid="delete-tenant-btn"
                >
                  <Trash2 size={16} /> Delete Tenant
                </NexusButton>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Create Tenant Modal */}
      <NexusModal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Create Tenant" description="Onboard a new customer organization with an initial admin user">
        <div className="space-y-4">
          <NexusInput
            label="Organization Name"
            value={form.name}
            onChange={(e) => {
              const name = e.target.value;
              setForm(p => ({ ...p, name, slug: autoSlug(name) }));
            }}
            placeholder="Acme Engineering"
            data-testid="tenant-name-input"
          />
          <NexusInput
            label="Industry"
            value={form.industry}
            onChange={(e) => setForm(p => ({ ...p, industry: e.target.value }))}
            placeholder="Oil & Gas"
          />
          <NexusInput
            label="Billing Email"
            value={form.billing_email}
            onChange={(e) => setForm(p => ({ ...p, billing_email: e.target.value }))}
            placeholder="billing@acme.com"
          />
          <NexusSelect
            label="Plan Tier"
            options={PLAN_OPTIONS}
            value={form.plan_tier}
            onChange={(v) => setForm(p => ({ ...p, plan_tier: v }))}
          />

          {/* Admin User Section */}
          <div className="pt-4 border-t border-nexus-border-subtle">
            <p className="text-sm font-medium text-nexus-text-primary mb-3 flex items-center gap-2">
              <Users size={14} className="text-nexus-accent-mint" /> Initial Admin User
            </p>
            <div className="space-y-3">
              <NexusInput
                label="Admin Name"
                value={form.admin_name}
                onChange={(e) => setForm(p => ({ ...p, admin_name: e.target.value }))}
                placeholder="John Doe"
                data-testid="tenant-admin-name"
              />
              <NexusInput
                label="Admin Email"
                type="email"
                value={form.admin_email}
                onChange={(e) => setForm(p => ({ ...p, admin_email: e.target.value }))}
                placeholder="john@acme.com"
                data-testid="tenant-admin-email"
              />
              <NexusInput
                label="Admin Password"
                type="password"
                value={form.admin_password}
                onChange={(e) => setForm(p => ({ ...p, admin_password: e.target.value }))}
                placeholder="Minimum 6 characters"
                data-testid="tenant-admin-password"
              />
            </div>
          </div>
        </div>
        <NexusModalFooter>
          <NexusButton variant="ghost" onClick={() => setShowCreate(false)}>Cancel</NexusButton>
          <NexusButton onClick={handleCreateTenant} isLoading={createMutation.isPending} data-testid="create-tenant-submit">Create</NexusButton>
        </NexusModalFooter>
      </NexusModal>
    </div>
  );
};

export default AdminTenants;
